import type {
  AutoCompleteRuleLastRunStatus,
  AutoCompleteTriggerType,
  NodeAutoCompleteRule,
} from '../../types/nodeAutoCompleteRule';

export interface NodeAutoCompleteRuleVO {
  id: number;
  name: string;
  enabled: number | boolean;
  trigger_type?: AutoCompleteTriggerType;
  triggerType?: AutoCompleteTriggerType;
  trigger_key?: string;
  triggerKey?: string;
  mq_vhost?: string;
  mqVhost?: string;
  mq_queue?: string;
  mqQueue?: string;
  prepare_script?: string;
  prepareScript?: string;
  prepare_timeout_sec?: number;
  prepareTimeoutSec?: number;
  prepare_retry_count?: number;
  prepareRetryCount?: number;
  decision_script?: string;
  decisionScript?: string;
  decision_timeout_sec?: number;
  decisionTimeoutSec?: number;
  decision_retry_count?: number;
  decisionRetryCount?: number;
  version?: number;
  linked_node_count?: number;
  linkedNodeCount?: number;
  linked_node_names?: string[];
  linkedNodeNames?: string[];
  last_normal_run_time?: string | null;
  lastNormalRunTime?: string | null;
  last_normal_run_status?: string | null;
  lastNormalRunStatus?: string | null;
  create_time?: string;
  createTime?: string;
  update_time?: string;
  updateTime?: string;
}

export interface NodeAutoCompleteRulePagedResultVO {
  list: NodeAutoCompleteRuleVO[];
  total: number;
  pageNum: number;
  pageSize: number;
}

export interface NodeAutoCompleteRuleSaveDTO {
  name: string;
  enabled: boolean;
  triggerType: AutoCompleteTriggerType;
  triggerKey?: string;
  mqVhost?: string;
  mqQueue?: string;
  prepareScript: string;
  prepareTimeoutSec: number;
  prepareRetryCount: number;
  decisionScript: string;
  decisionTimeoutSec: number;
  decisionRetryCount: number;
}

export interface NodeAutoCompleteRuleUpdateDTO extends NodeAutoCompleteRuleSaveDTO {
  id: number;
}

export interface NodeAutoCompleteRuleStepTestResultVO {
  success: boolean;
  result_json?: string;
  resultJson?: string;
  duration_ms?: number;
  durationMs?: number;
  logs?: string[];
  error_message?: string;
  errorMessage?: string;
}

export interface NodeAutoCompleteRuleNodeTestResultVO {
  project_id?: number;
  projectId?: number;
  schedule_id?: number;
  scheduleId?: number;
  schedule_version?: number;
  scheduleVersion?: number;
  spec_schedule_id?: number;
  specScheduleId?: number;
  spec_name?: string;
  specName?: string;
  milestone_id?: number;
  milestoneId?: number;
  milestone_name?: string;
  milestoneName?: string;
  schedule_milestone_id?: number | null;
  scheduleMilestoneId?: number | null;
  success: boolean;
  decision_action?: string;
  decisionAction?: string;
  prepare?: NodeAutoCompleteRuleStepTestResultVO;
  decision?: NodeAutoCompleteRuleStepTestResultVO;
  error_message?: string;
  errorMessage?: string;
}

export interface NodeAutoCompleteRuleTestResultVO {
  success: boolean;
  run_id?: number | null;
  runId?: number | null;
  run_source?: string;
  runSource?: string;
  mode?: string;
  prepare?: NodeAutoCompleteRuleStepTestResultVO;
  decision?: NodeAutoCompleteRuleStepTestResultVO;
  nodes?: NodeAutoCompleteRuleNodeTestResultVO[];
  schedule_count?: number;
  scheduleCount?: number;
  node_total?: number;
  nodeTotal?: number;
  node_success_count?: number;
  nodeSuccessCount?: number;
  node_failed_count?: number;
  nodeFailedCount?: number;
  error_message?: string;
  errorMessage?: string;
}

export interface NodeAutoCompleteRuleTestProjectVO {
  id: number;
  name: string;
  code?: string;
  status?: string;
  matching_schedule_count?: number;
  matchingScheduleCount?: number;
  matching_schedule_labels?: string[];
  matchingScheduleLabels?: string[];
}

export interface NodeAutoCompleteRuleTestProjectPagedVO {
  list: NodeAutoCompleteRuleTestProjectVO[];
  total: number;
  pageNum: number;
  pageSize: number;
}

export interface NodeAutoCompleteRuleScriptTestDTO {
  step: 'prepare' | 'decision';
  script: string;
  context?: unknown;
  timeoutSec?: number;
}

/** 测试使用已保存规则脚本；不再传脚本正文 */
export interface NodeAutoCompleteRuleTestDTO {
  mode: 'manual' | 'schedule';
  ruleId: number;
  triggerPayload?: unknown;
  nodeContext?: unknown;
  projectIds?: number[];
}

export interface NodeAutoCompleteRuleRunNodeLogVO {
  id: number;
  run_id?: number;
  runId?: number;
  rule_id?: number;
  ruleId?: number;
  project_id?: number | null;
  projectId?: number | null;
  schedule_id?: number | null;
  scheduleId?: number | null;
  spec_schedule_id?: number | null;
  specScheduleId?: number | null;
  project_name?: string;
  projectName?: string;
  schedule_name?: string;
  scheduleName?: string;
  spec_name?: string;
  specName?: string;
  milestone_node_id?: number;
  milestoneNodeId?: number;
  milestone_node_name?: string;
  milestoneNodeName?: string;
  schedule_milestone_id?: number | null;
  scheduleMilestoneId?: number | null;
  status?: string;
  prepare_status?: string;
  prepareStatus?: string;
  prepare_input_json?: string | null;
  prepareInputJson?: string | null;
  prepare_output_json?: string | null;
  prepareOutputJson?: string | null;
  prepare_duration_ms?: number;
  prepareDurationMs?: number;
  prepare_error_message?: string;
  prepareErrorMessage?: string;
  decision_status?: string;
  decisionStatus?: string;
  decision_input_json?: string | null;
  decisionInputJson?: string | null;
  decision_output_json?: string | null;
  decisionOutputJson?: string | null;
  decision_action?: string;
  decisionAction?: string;
  decision_duration_ms?: number;
  decisionDurationMs?: number;
  decision_error_message?: string;
  decisionErrorMessage?: string;
  create_time?: string;
  createTime?: string;
  update_time?: string;
  updateTime?: string;
}

export interface NodeAutoCompleteRuleRunLogVO {
  id: number;
  rule_id?: number;
  ruleId?: number;
  rule_name?: string;
  ruleName?: string;
  rule_version?: number;
  ruleVersion?: number;
  trigger_type?: string;
  triggerType?: string;
  trigger_source?: string;
  triggerSource?: string;
  run_source?: string;
  runSource?: string;
  status?: string;
  node_total?: number;
  nodeTotal?: number;
  node_success_count?: number;
  nodeSuccessCount?: number;
  node_failed_count?: number;
  nodeFailedCount?: number;
  duration_ms?: number;
  durationMs?: number;
  error_message?: string;
  errorMessage?: string;
  create_time?: string;
  createTime?: string;
  nodes?: NodeAutoCompleteRuleRunNodeLogVO[];
}

export interface NodeAutoCompleteRuleRunLogPagedVO {
  list: NodeAutoCompleteRuleRunLogVO[];
  total: number;
  pageNum: number;
  pageSize: number;
}

export interface NodeAutoCompleteRuleVersionVO {
  id: number;
  rule_id?: number;
  ruleId?: number;
  version: number;
  name?: string;
  enabled?: number | boolean;
  trigger_type?: string;
  triggerType?: string;
  trigger_key?: string;
  triggerKey?: string;
  mq_vhost?: string;
  mqVhost?: string;
  mq_queue?: string;
  mqQueue?: string;
  prepare_script?: string;
  prepareScript?: string;
  prepare_timeout_sec?: number;
  prepareTimeoutSec?: number;
  prepare_retry_count?: number;
  prepareRetryCount?: number;
  decision_script?: string;
  decisionScript?: string;
  decision_timeout_sec?: number;
  decisionTimeoutSec?: number;
  decision_retry_count?: number;
  decisionRetryCount?: number;
  create_time?: string;
  createTime?: string;
}

export interface NodeAutoCompleteRuleVersionPagedVO {
  list: NodeAutoCompleteRuleVersionVO[];
  total: number;
  pageNum: number;
  pageSize: number;
}

export interface NodeAutoCompleteRuleTriggerItemVO {
  rule_id?: number;
  ruleId?: number;
  rule_name?: string;
  ruleName?: string;
  rule_version?: number;
  ruleVersion?: number;
  success?: boolean;
  run_id?: number | null;
  runId?: number | null;
  schedule_count?: number;
  scheduleCount?: number;
  node_total?: number;
  nodeTotal?: number;
  node_success_count?: number;
  nodeSuccessCount?: number;
  node_failed_count?: number;
  nodeFailedCount?: number;
  error_message?: string;
  errorMessage?: string;
}

export interface NodeAutoCompleteRuleTriggerResultVO {
  success?: boolean;
  trigger_type?: string;
  triggerType?: string;
  trigger_key?: string;
  triggerKey?: string;
  mq_vhost?: string;
  mqVhost?: string;
  mq_queue?: string;
  mqQueue?: string;
  matched_rule_count?: number;
  matchedRuleCount?: number;
  success_rule_count?: number;
  successRuleCount?: number;
  failed_rule_count?: number;
  failedRuleCount?: number;
  error_message?: string;
  errorMessage?: string;
  rules?: NodeAutoCompleteRuleTriggerItemVO[];
}

export function mapNodeAutoCompleteRuleVO(vo: NodeAutoCompleteRuleVO): NodeAutoCompleteRule {
  const rawStatus = (vo.lastNormalRunStatus ?? vo.last_normal_run_status ?? '').toLowerCase();
  const runTime = vo.lastNormalRunTime ?? vo.last_normal_run_time ?? undefined;
  let lastRunStatus: AutoCompleteRuleLastRunStatus = 'none';
  if (runTime) {
    lastRunStatus = rawStatus === 'failed' ? 'failed' : 'success';
  }

  return {
    id: String(vo.id),
    name: vo.name ?? '',
    enabled: vo.enabled === true || vo.enabled === 1,
    triggerType: (vo.triggerType ?? vo.trigger_type ?? 'api') as AutoCompleteTriggerType,
    triggerKey: vo.triggerKey ?? vo.trigger_key,
    mqVhost: vo.mqVhost ?? vo.mq_vhost,
    mqQueue: vo.mqQueue ?? vo.mq_queue,
    prepareScript: vo.prepareScript ?? vo.prepare_script ?? '',
    prepareTimeoutSec: vo.prepareTimeoutSec ?? vo.prepare_timeout_sec ?? 100,
    prepareRetryCount: vo.prepareRetryCount ?? vo.prepare_retry_count ?? 3,
    decisionScript: vo.decisionScript ?? vo.decision_script ?? '',
    decisionTimeoutSec: vo.decisionTimeoutSec ?? vo.decision_timeout_sec ?? 100,
    decisionRetryCount: vo.decisionRetryCount ?? vo.decision_retry_count ?? 3,
    version: vo.version ?? 1,
    linkedNodeCount: vo.linkedNodeCount ?? vo.linked_node_count ?? 0,
    linkedNodeNames: vo.linkedNodeNames ?? vo.linked_node_names ?? [],
    lastRun: {
      status: lastRunStatus,
      time: runTime || undefined,
    },
    createTime: vo.createTime ?? vo.create_time,
    updateTime: vo.updateTime ?? vo.update_time,
  };
}

export function buildNodeAutoCompleteRuleSaveBody(
  form: NodeAutoCompleteRuleSaveDTO,
): NodeAutoCompleteRuleSaveDTO {
  return {
    name: form.name.trim(),
    enabled: form.enabled,
    triggerType: form.triggerType,
    triggerKey: form.triggerType === 'api' ? form.triggerKey?.trim() : undefined,
    mqVhost: form.triggerType === 'mq' ? form.mqVhost : undefined,
    mqQueue: form.triggerType === 'mq' ? form.mqQueue : undefined,
    prepareScript: form.prepareScript,
    prepareTimeoutSec: form.prepareTimeoutSec,
    prepareRetryCount: form.prepareRetryCount,
    decisionScript: form.decisionScript,
    decisionTimeoutSec: form.decisionTimeoutSec,
    decisionRetryCount: form.decisionRetryCount,
  };
}
